import React, { useState } from 'react';
import { Tabs, Card, Avatar, Button, Statistic, Tooltip, List, Progress, Popconfirm, Checkbox, Row, Col } from 'antd';
import { EnvironmentFilled, QuestionCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { Link, useHistory, useLocation } from 'react-router-dom';
import gql from 'graphql-tag';
import { useQuery } from '@apollo/client';
import moment from 'moment';
import { usePosition } from 'use-position';
import { useFetch } from '../../../utils/useFetch';
import { sortShippingGroups, locationApi } from '../../../utils/sortShippingGroups';

import { COMPANY_PACKING_DAYS_GUARANTEE } from '../../../config';

import Error from '../../../components/Error';
import Loading from '../../../components/Loading';
import CountryIcon from '../../../components/CountryIcon';

import './index.css';
import Pullable from '../../../components/Pullable';
import { ShippingGroup, ShippingGroupType } from '../../../utils/types';
import { GROUP_TYPE } from '../../../utils/constants';

const { Countdown } = Statistic;
const { TabPane } = Tabs;
const { Meta } = Card;

function useURLQuery() {
  return new URLSearchParams(useLocation().search);
}

const SHIPPING_GROUPS_QUERY = gql`
  query shippingGroupsQuery($type: ShippingGroupType, $status: ShippingGroupStatus) {
    shippingGroups(type: $type, status: $status) {
      id
      name
      state
      country
      city
      zipCode
      status
      type
      deadline
      notice
      countdownSeconds
      minWeight
      hasFixedLeader
      originalShippingGroupId
      isPrivate
      leader {
        id
        name
      }
      totalWeight
      latitude
      longitude
    }
  }
`;

function Groups() {
  const query = useURLQuery();
  const tab = query.get('tab') || ShippingGroupType.Regular;

  const history = useHistory();

  const [ hidePrivateGroups, setHidePrivateGroups ] = useState(localStorage.getItem('HIDE_PRIVATE_GROUPS') === 'true' ? true : false);

  const checkbox = (
    <Row justify="center">
      <Col>
        <Checkbox
          checked={hidePrivateGroups}
          onChange={(e)=> {
            setHidePrivateGroups(e.target.checked);
            localStorage.setItem('HIDE_PRIVATE_GROUPS', JSON.stringify(e.target.checked));
          }}
        >
          只看公共团
        </Checkbox>
      </Col>
    </Row>
  );

  return (
    <div className="Groups">
      {/* <Link to="/groups/add" style={{ position: 'absolute', right: '1rem', zIndex: 1 }}> */}
      <div style={{ position: 'absolute', right: '1rem', zIndex: 1 }}>
        <Popconfirm
          title="确定要开新团吗？拼邮团截团后会自动生成新的同名团，并把未入库的包裹都加到新的团，因此不需要手动开新团。但如果你需要开一个新的区域的团或私人团，是完全可以的，请点开新团"
          onConfirm={() => history.push('/groups/add')}
          okText="开新团"
          cancelText="取消"
        >
          <Button shape="circle" type="primary" icon={<PlusOutlined />} />
        </Popconfirm>
      </div>
      {/* </Link> */}

      <Tabs
        hideAdd
        type="card"
        onChange={key => {
          history.push(`/groups?tab=${key}`);
        }}
        activeKey={tab}
        style={{ marginTop: '5px' }}
      >
        {Object.entries(GROUP_TYPE).map(([type, value]) =>
          <TabPane tab={value.text} key={type}>
            {checkbox}
            <GroupList type={type} hidePrivateGroups={hidePrivateGroups} />
          </TabPane>
        )}
      </Tabs>
    </div>
  );
}

export default Groups;

function GroupList({ type, hidePrivateGroups }: { type: string, hidePrivateGroups: boolean }) {
  const position = usePosition();
  const ipPositionRequest = useFetch(locationApi);
  const { error, loading, data, refetch } = useQuery(SHIPPING_GROUPS_QUERY, {
    variables: { type, status: 'OPEN' },
    fetchPolicy: 'cache-and-network',
  });

  if (loading) return <Loading />;
  if (error) return <Error />;

  const groups = sortShippingGroups(data.shippingGroups, position, ipPositionRequest).filter((g) => hidePrivateGroups ? !g.isPrivate : true);

  return (
    <Pullable onRefresh={refetch}>
      <List
        grid={{ gutter: 16, column: 1 }}
        style={{ marginTop: '10px' }}
        dataSource={groups}
        renderItem={(group: ShippingGroup) => {
          const totalWeight = group.totalWeight || 0;
          const goalWeight = group.minWeight || 1;

          const weightPercent = Math.floor((totalWeight / goalWeight) * 100) || 0;
          const hasReachedWeight = totalWeight >= goalWeight;
          const estimatedDaysToReachWeight = 7 - Math.floor((totalWeight / goalWeight) * 7);

          // in case BE hasn't set deadline yet
          const calculatedDeadline =
            group.countdownSeconds && moment().add(group.countdownSeconds, 'seconds');

          const hasPassedDeadline =
            group.status !== 'OPEN' ||
            (group.deadline && moment().isAfter(moment(group.deadline))) ||
            (hasReachedWeight && !group.deadline && group.countdownSeconds === 0);

          const hasClosed = group.status !== 'OPEN';

          return (
            <List.Item>
              <Card
                key={group.id}
                bordered={false}
                hoverable
                style={{ width: '100%', background: '#FEF5DD', marginBottom: '1rem' }}
                cover={
                  <div className="Groups-groupCard-cover">
                    <div className="Groups-groupCard-title">
                      {group.isPrivate && '🔒 '}
                      {group.name}
                    </div>
                    <div className="Groups-groupCard-subtitle">
                      <EnvironmentFilled style={{ marginRight: '5px' }} />
                      {group.city}, {group.state} {group.zipCode}{' '}
                      <CountryIcon iso={group.country} />
                    </div>
                  </div>
                }
              >
                <Meta
                  avatar={
                    <Avatar
                      src={`https://robohash.org/${group.leader ? group.leader.name : '待定'}`}
                    />
                  }
                  title={
                    <span>
                      {`${
                        group.hasFixedLeader
                          ? '固定团长'
                          : group.status === 'OPEN'
                          ? '临时团长'
                          : '收货团长'
                      }: ${group.leader ? group.leader.name : '无'}`}
                      &nbsp;
                      <Tooltip
                        title={
                          group.hasFixedLeader
                            ? '固定团长的收货地址是固定的，这样每次货到了，大家都可以去同一个地方取货，非常方便。长期招募固定团长，有意请详询客服 💖'
                            : '建团的人自动成为临时团长，截团时会自动把包裹总重量最大的人设为收货团长，包裹最终会寄到收货团长家'
                        }
                      >
                        <QuestionCircleOutlined />
                      </Tooltip>
                    </span>
                  }
                />
                <Progress percent={weightPercent} showInfo={false} />
                <div className="Groups-group-progressDesc">
                  <div>
                    {weightPercent}% of {goalWeight}kg
                  </div>
                  <div>
                    {!hasReachedWeight && !group.deadline && !hasClosed && (
                      <span>预计{estimatedDaysToReachWeight}天凑满重量</span>
                    )}
                    {!hasReachedWeight && group.deadline && !hasPassedDeadline && (
                      <Countdown
                        suffix={
                          <span>
                            截团&nbsp;
                            <Tooltip title="该团曾达到过最低重量并自动开启了截团倒计时，后来有包裹被移出导致重量不足，但倒计时仍保留，若在倒计时结束之前可以凑满重量，则仍可按时截团；否则会一直等到凑满重量再截团">
                              <QuestionCircleOutlined />
                            </Tooltip>
                          </span>
                        }
                        valueStyle={{ fontSize: '14px' }}
                        value={group.deadline}
                        format="D 天 HH:mm:ss"
                      />
                    )}
                    {!hasReachedWeight && group.deadline && hasPassedDeadline && !hasClosed && (
                      <span>
                        凑满重量马上截团&nbsp;
                        <Tooltip title="该团曾达到过最低重量并自动开启了截团倒计时，后来有包裹被移出导致重量不足且倒计时已结束，这种情况下等一旦凑满重量就会马上截团">
                          <QuestionCircleOutlined />
                        </Tooltip>
                      </span>
                    )}
                    {hasReachedWeight && !hasPassedDeadline && !hasClosed && (
                      <Countdown
                        suffix={
                          <span>
                            截团&nbsp;
                            <Tooltip title="凑满重量后会根据团设置自动开启截团倒计时，倒计时期间仍然可以跟团。但截团后再入库的包裹都无法跟团发走，会被系统自动添加到新开的同名团，后续可以修改包裹加到别的团">
                              <QuestionCircleOutlined />
                            </Tooltip>
                          </span>
                        }
                        valueStyle={{ fontSize: '14px' }}
                        value={group.deadline || calculatedDeadline}
                        format="D 天 HH:mm:ss"
                      />
                    )}
                    {hasReachedWeight && hasPassedDeadline && !hasClosed && (
                      <span>
                        马上截团&nbsp;
                        <Tooltip title="还有几分钟的时间可以添加已入库的包裹，但有可能会添加失败">
                          <QuestionCircleOutlined />
                        </Tooltip>
                      </span>
                    )}
                    {hasClosed && (
                      <span>
                        已截团, {COMPANY_PACKING_DAYS_GUARANTEE}个工作日内打包&nbsp;
                        <Tooltip title="截团后再入库的包裹都无法跟团发走，会被系统自动添加到新开的同名团，后续可以修改包裹加到别的团">
                          <QuestionCircleOutlined />
                        </Tooltip>
                      </span>
                    )}
                  </div>
                </div>

                <Link to={`/groups/${group.id}`}>
                  <Button block danger>
                    {hasClosed ? '查看' : '加入'}拼邮团
                  </Button>
                </Link>
              </Card>
            </List.Item>
          );
        }}
      />
    </Pullable>
  );
}
